/**
 * EPCS Status Callout
 *
 * Displays the current EPCS (Electronic Prescribing for Controlled Substances)
 * activation status within the e-prescription user section.
 *
 * - Pending: Admin hasn't activated EPCS yet
 * - Active: EPCS activated, user needs to set up TFA in DoseSpot
 */

import { CheckCircle, HourglassEmpty, Info } from '@mui/icons-material';
import { alpha, Box, Paper, Typography } from '@mui/material';

interface EpcsStatusCalloutProps {
  status: 'pending' | 'active';
}

export function EpcsStatusCallout({ status }: EpcsStatusCalloutProps) {
  if (status === 'pending') {
    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          borderColor: 'grey.400',
          bgcolor: theme => alpha(theme.palette.grey[500], 0.05),
          borderRadius: 2,
        }}
      >
        <HourglassEmpty sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            EPCS activation pending
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Our team has been automatically notified and will enable controlled substance prescribing
            for your account. This usually takes just a few minutes.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        px: 2.5,
        py: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        borderColor: 'success.light',
        bgcolor: theme => alpha(theme.palette.success.main, 0.05),
        borderRadius: 2,
      }}
    >
      <CheckCircle sx={{ fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          EPCS has been activated for your account
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          To prescribe controlled substances, you need to set up two-factor authentication (TFA) in
          DoseSpot.
        </Typography>

        {/* TFA tip box */}
        <Paper
          variant="outlined"
          sx={{
            mt: 1.5,
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            bgcolor: theme => alpha(theme.palette.info.main, 0.06),
            borderColor: theme => alpha(theme.palette.info.main, 0.3),
            borderRadius: 1.5,
          }}
        >
          <Info sx={{ fontSize: 16, color: 'info.main', mt: 0.25, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            When you open DoseSpot, look for the TFA setup prompt to complete two-factor
            authentication for controlled substance prescribing.
          </Typography>
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
        >
          If DoseSpot is already open, close and reopen it to see the TFA setup prompt.
        </Typography>
      </Box>
    </Paper>
  );
}
